import { useState } from 'react';
import { Star } from 'lucide-react';
import { toast } from 'sonner';
import * as api from '../lib/api';
import { Dialog, DialogSheetContent, DialogSheetHeader, DialogSheetBody, DialogDescription } from './ui/dialog';

// One-time, dismissable feedback prompt — shown once per user (see App.js's
// needsFeedback) and never again after either a submit or "Ahora no,
// gracias" (both permanently clear it server-side, see POST /feedback).
// Unlike AcquisitionSourceModal this is genuinely optional: backdrop click
// dismisses it too, same as the explicit button.
export default function FeedbackModal({ onDone }) {
    const [rating, setRating] = useState(0);
    const [hoverRating, setHoverRating] = useState(0);
    const [text, setText] = useState('');
    const [busy, setBusy] = useState(false);

    const handleDismiss = async () => {
        if (busy) return;
        setBusy(true);
        try {
            await api.dismissFeedback();
        } catch (_) { /* best effort — don't block them from closing this */ }
        finally {
            onDone();
        }
    };

    const handleSubmit = async () => {
        if (!rating || busy) return;
        setBusy(true);
        try {
            await api.submitFeedback(rating, text.trim());
            toast.success('¡Gracias por tu feedback!');
            onDone();
        } catch (e) {
            toast.error('No se pudo enviar. Intenta de nuevo.');
            setBusy(false);
        }
    };

    return (
        <Dialog open onOpenChange={(open) => { if (!open) handleDismiss(); }}>
            <DialogSheetContent compact data-testid="feedback-modal">
                <DialogDescription className="sr-only">Comparte tu opinión sobre MiPisto</DialogDescription>

                <DialogSheetHeader
                    title="Tu opinión"
                    onCancel={handleDismiss}
                    cancelLabel="Ahora no"
                    cancelTestId="feedback-dismiss-btn"
                    action={
                        <button
                            type="button"
                            data-testid="feedback-submit-btn"
                            onClick={handleSubmit}
                            disabled={!rating || busy}
                            className="text-[15px] font-semibold text-violet-400 px-2 py-2 -mr-2 rounded-lg transition-colors active:scale-[0.97] disabled:opacity-40"
                        >
                            {busy ? (
                                <div className="w-4 h-4 border-2 border-current/30 border-t-current rounded-full animate-spin" />
                            ) : 'Enviar'}
                        </button>
                    }
                />

                <DialogSheetBody className="space-y-6">
                    <div className="text-center">
                        <h2 className="text-xl font-bold text-white mb-1" style={{ fontFamily: 'Poppins' }}>
                            ¿Qué te parece MiPisto? 💬
                        </h2>
                        <p className="text-slate-400 text-sm">
                            Tu opinión nos ayuda a mejorar. Toma 10 segundos.
                        </p>
                    </div>

                    <div className="flex justify-center gap-2" data-testid="feedback-stars">
                        {[1, 2, 3, 4, 5].map((n) => (
                            <button
                                key={n}
                                type="button"
                                data-testid={`feedback-star-${n}`}
                                onClick={() => setRating(n)}
                                onMouseEnter={() => setHoverRating(n)}
                                onMouseLeave={() => setHoverRating(0)}
                                className="p-1 transition-transform duration-150 hover:scale-110 active:scale-95"
                            >
                                <Star
                                    className={`w-10 h-10 ${
                                        n <= (hoverRating || rating)
                                            ? 'fill-amber-400 text-amber-400'
                                            : 'text-slate-600'
                                    }`}
                                />
                            </button>
                        ))}
                    </div>

                    <textarea
                        data-testid="feedback-text-input"
                        value={text}
                        onChange={(e) => setText(e.target.value)}
                        placeholder="¿Algo que quieras contarnos? (opcional)"
                        rows={3}
                        maxLength={2000}
                        className="w-full bg-slate-950/50 border border-slate-700 focus:border-violet-500/50 rounded-xl px-3.5 py-2.5 text-white placeholder:text-slate-400 text-sm outline-none resize-none"
                    />
                </DialogSheetBody>
            </DialogSheetContent>
        </Dialog>
    );
}
